<template>
  <AtomLink v-if="slide" :url="slide.url" class="text-black group relative flex flex-col w-full rounded-t-xl overflow-hidden">
    <figure v-if="slide.highlightImage || slide.image">
      <MoleculePicture
        class="block"
        :image="slide.highlightImage || slide.image"
        :class="{ 'opacity-50': isPlaced(slide.animalAvailability) || isReserved(slide.animalAvailability) || isReactionStop(slide.animalAvailability) }"
      />
    </figure>
    <div
      v-else
      class="c-carousel-slider-animal-placeholder-image flex items-center justify-center"
      :class="{
        'bg-app-gray': isPlaced(slide.animalAvailability) || isReactionStop(slide.animalAvailability) || isReserved(slide.animalAvailability),
        'bg-app-green': !isPlaced(slide.animalAvailability) && !isReactionStop(slide.animalAvailability) && !isReserved(slide.animalAvailability),
      }"
    >
      <AtomIcon
        v-if="slide.animalCategory && slide.animalCategory.icon"
        :icon="slide.animalCategory.icon.slug"
        class="w-32 h-32"
        :class="{
          'text-app-gray-silverstone': isPlaced(slide.animalAvailability) || isReactionStop(slide.animalAvailability) || isReserved(slide.animalAvailability),
          'text-app-green-200': !isPlaced(slide.animalAvailability) && !isReactionStop(slide.animalAvailability) && !isReserved(slide.animalAvailability),
        }"
      />
    </div>
    <div class="absolute left-0 top-0">
      <MoleculeBadgeInCorner v-if="isReserved(slide.animalAvailability)" background="bg-app-gray-silverstone" color="text-white" :in-carousel="true">
        {{ t_('status-reserved') }}
      </MoleculeBadgeInCorner>
      <MoleculeBadgeInCorner v-else-if="isPlaced(slide.animalAvailability)" background="bg-app-gray-silverstone" color="text-white" :in-carousel="true">
        {{ t_('status-placed') }}
      </MoleculeBadgeInCorner>
      <MoleculeBadgeInCorner v-else-if="isReactionStop(slide.animalAvailability)" background="bg-app-gray-silverstone" color="text-white" :in-carousel="true">
        {{ t_('status-reaction-stop') }}
      </MoleculeBadgeInCorner>
    </div>
    <div class="flex flex-auto flex-col px-6 py-5 w-full bg-app-green-200 border-t-2 border-app-lime rounded-b-xl">
      <div class="flex items-center font-bold leading-none">
        <ul class="additional-animal-info grid gap-1 grid-cols-2 w-full">
          <li v-if="slide.animalGender" class="justify-left flex">
            <div class="flex items-center">
              <span>{{ t_(slide.animalGender) }}</span>
              <AtomIcon :icon="genderIconMap[slide.animalGender] || genderIconMap.fallback" class="ml-1 w-4 h-4 text-app-green" />
            </div>
          </li>
          <li v-if="!isFound && slide.birthdate" class="justify-left flex">
            <div class="flex items-center">
              <span>{{ d_(slide.birthdate, 'short-date') }}</span>
              <AtomIconBirthdayCake class="ml-1 w-4 h-4 text-app-green" />
            </div>
          </li>
          <li v-if="!isAvailable && animalCity" class="justify-left flex">
            <div class="flex items-center">
              <span>{{ animalCity }}</span>
              <AtomIconAppLocationPin class="ml-1 w-4 h-4 text-app-green" />
            </div>
          </li>
          <!-- todo: check styling and spacing for new "animalFoundDate" -->
          <li v-if="isFound && slide.animalFoundDate" class="col-span-2">
            <div class="flex items-center">
              <span>{{ t_('found-on', { date: $d(new Date(slide.animalFoundDate), 'short-date') }) }}</span>
            </div>
          </li>
        </ul>
      </div>
      <div class="mb-3 mt-2 border-b-2 border-white" />
      <div class="mb-2">
        <h3 class="mb-2 text-2xl font-bold leading-none uppercase">{{ slide.title }}</h3>
        <div v-html="truncate(stripTags(slide.introduction), { length: 256 }) || '&nbsp;'" />
      </div>
      <span class="mt-4 mt-auto font-bold">
        <span class="inline-flex items-center border-b-2 border-app-green">
          <div class="text-black text-sm font-bold">
            {{ t_('link-profile') }}
          </div>
          <AtomIconArrowRight class="ml-3 w-3 text-app-green transform group-hover:translate-x-1 transition duration-200" />
        </span>
      </span>
    </div>
  </AtomLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import uniq from 'lodash/uniq'
import { setupTranslationPrefixer, stripTags, truncate } from '~/composables/util'
import { isPlaced, isReactionStop, isReserved } from '@/assets/animal'

interface Props {
  slide: any // Specify more specific type if possible
  translationGroup?: string
}

const props = defineProps<Props>()

const genderIconMap = { male: 'app-male', female: 'app-female', fallback: 'app-unknown-gender' }

const { t_, d_ } = setupTranslationPrefixer('CarouselSlideAnimal', props.translationGroup)

const animalCity = computed(() => {
  const city = props.slide?.animalMap?.parts?.city || props.slide?.animalAddress?.city
  return uniq(city?.split(',').map((value) => value.trim()) || []).join(', ')
})

const isAvailable = computed(() => props.slide?.animalType === 'available')
const isFound = computed(() => props.slide?.animalType === 'found')
</script>

<style lang="scss" scoped>
.c-carousel-slider-animal-placeholder-image {
  $ratio: calc(519 / 453);
  height: calc((100vw - 24px - 24px) / #{$ratio});

  @screen sm {
    height: 237px;
  }

  @screen md {
    height: 289px;
  }

  @screen lg {
    height: 257px;
  }

  @screen xl {
    height: 330px;
  }

  @screen 2xl {
    height: 259px;
  }
}
</style>
